// Detailpaneel van één taak — een lade rechts, geen dialoog.
//
// TaskDetailDialog in Mijn Week legde zich midden over het scherm: precies over de
// tijdlijn waar je het tijdens de dagstart met elkaar over hebt. Een Drawer houdt de
// planning in beeld en levert focus-trap en Escape gratis mee.

import React, { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { isEigenPrive, zetPrive } from '../../utils/priveTaken';
import toast from 'react-hot-toast';
import {
  Box, Button, Chip, Divider, Drawer, IconButton, ListItemText, MenuItem, Select, TextField, Typography, Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  DeleteOutline as DeleteOutlineIcon,
  OpenInNew as OpenInNewIcon,
  Send as SendIcon,
  TaskAlt as TaskAltIcon,
  Today as TodayIcon,
} from '@mui/icons-material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FeedbackVeld from './FeedbackVeld';

import { effectiveTaskHours } from '../../utils/capacityCalc';
import { dagenOverDeadline, werkdagenInBereik } from '../../utils/dagstartLanes';
import { taakKleur } from './TaskBlock';
import { formatDateISO } from '../../utils/weekUtils';
import { bevestig } from '../../utils/bevestig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson, meldFout } from '../../utils/apiFout';
import { BACKEND_URL } from '../../utils/apiConfig';

/** Zelfde lijst als de snelmenu's elders; PrioNr draagt prioriteit én status. */
const PRIO_OPTIES = [
  'Prio 1', 'Prio 2', 'Prio 3', 'Prio 4', 'Geen prio',
  'Wacht op feedback', 'Nog niet van toepassing',
  'Afspraak', 'Vanallesuurtje', 'Deadline',
];

const datumLabel = (iso) => {
  if (!iso) return '—';
  const [j, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!j) return iso;
  return new Date(j, m - 1, d).toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' });
};

/** Vier waarden in Airtable; Hard en Klant tellen als harde belofte (zie dagstartLanes). */
const DEADLINE_SOORTEN = ['Hard', 'Klant', 'Zacht', 'Intern'];

/**
 * Een invoerveld dat pas opslaat als je het verlaat (of Enter drukt); Escape zet terug.
 *
 * Nadrukkelijk niet per toetsaanslag: Airtable staat 5 requests per seconde toe over de
 * héle base en een 429 zet die base 30 seconden op slot — dan ligt de hele app plat, niet
 * alleen dit scherm. "4" intypen zou anders al twee schrijfacties zijn.
 *
 * `onBewaar` mag `false` teruggeven als de invoer niet deugt; dan zetten we het veld terug
 * op de opgeslagen waarde, zodat er nooit iets in beeld staat wat niet is opgeslagen.
 */
function BewaarVeld({ waarde, type = 'text', breedte = 110, disabled, label, plaatshouder, onBewaar }) {
  const [tekst, setTekst] = useState(waarde ?? '');
  useEffect(() => { setTekst(waarde ?? ''); }, [waarde]);

  const bewaarNu = () => {
    if (String(tekst) === String(waarde ?? '')) return;
    if (onBewaar(tekst) === false) setTekst(waarde ?? '');
  };

  return (
    <TextField
      size="small"
      type={type}
      value={tekst}
      disabled={disabled}
      placeholder={plaatshouder}
      inputProps={{ 'aria-label': label }}
      onChange={(e) => setTekst(e.target.value)}
      onBlur={bewaarNu}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
        // Escape mag hier niet doorlopen naar de Drawer: die zou dichtklappen terwijl je
        // wijziging niet is opgeslagen, en dat zie je pas als je het paneel weer opent.
        if (e.key === 'Escape') { e.stopPropagation(); setTekst(waarde ?? ''); }
      }}
      sx={{ width: breedte, '& .MuiInputBase-input': { fontSize: '0.82rem', py: 0.6 } }}
    />
  );
}

const Regel = ({ label, children }) => (
  <Box sx={{ display: 'flex', gap: 1, py: 0.6, alignItems: 'baseline' }}>
    <Typography sx={{ width: 96, flexShrink: 0, fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700 }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1, minWidth: 0, fontSize: '0.82rem', color: '#2d3335' }}>{children}</Box>
  </Box>
);

export default function TaskDetailDrawer({
  taak,
  open,
  onClose,
  onWijzig,
  personen = [],
  onNaarVandaag,
  onDupliceer,
  onNaarProject,
  onVerwijderd,
  onLokaal,
  regels,
}) {
  // 🔒 Privé: verberg deze taak op het dagstart-scherm van collega's.
  const { instance: msalVoorPrive } = useMsal();
  const priveEmail = msalVoorPrive.getActiveAccount()?.username || '';
  const [privePending, setPrivePending] = useState(false);
  const [priveAan, setPriveAan] = useState(() => isEigenPrive(taak?.id, priveEmail));
  useEffect(() => { setPriveAan(isEigenPrive(taak?.id, priveEmail)); }, [taak?.id, priveEmail]);
  const wisselPrive = async () => {
    if (privePending || !taak?.id) return;
    setPrivePending(true);
    try {
      await zetPrive(taak.id, !priveAan, priveEmail);
      setPriveAan(!priveAan);
    } catch { /* melding hieronder blijft uit — volgende poging kan gewoon */ }
    finally { setPrivePending(false); }
  };

  const [bezig, setBezig] = useState(false);

  if (!taak) return null;

  const kleur = taakKleur(taak);
  const uitOutlook = Boolean(taak.uitOutlook);
  const dagen = werkdagenInBereik(taak.startDate, taak.endDate);
  const { val: uren, estimated } = effectiveTaskHours(taak, regels);
  // Een Outlook-blok draagt bewust hours: 0 (de agenda-uren tellen al via de
  // capaciteitsrij) — de schatter viel daardoor terug op de standaard 4u en
  // het paneel toonde elke afspraak als "4u". De echte duur staat gewoon in
  // de begin-/eindtijd.
  const afspraakUren = (() => {
    if (!taak.startTime || !taak.endTime) return null;
    const [sh, sm] = String(taak.startTime).split(':').map(Number);
    const [eh, em] = String(taak.endTime).split(':').map(Number);
    if ([sh, sm, eh, em].some((n) => !Number.isFinite(n))) return null;
    const d = (eh * 60 + em - (sh * 60 + sm)) / 60;
    return d > 0 ? d : null;
  })();
  const afspraakUrenLabel = afspraakUren === null ? '—'
    : `${(Math.round(afspraakUren * 100) / 100).toString().replace('.', ',')}u`;
  const perDag = dagen.length > 1 ? (uren / dagen.length).toFixed(1) : uren;
  const over = dagenOverDeadline(taak, formatDateISO(new Date()));

  const doe = async (velden, melding, terug) => {
    setBezig(true);
    const gelukt = await onWijzig(taak.id, velden, { melding, terug });
    setBezig(false);
    if (gelukt) onClose();
  };

  /**
   * Taak verwijderen. Onomkeerbaar: het record verdwijnt uit Airtable en komt
   * ook niet terug met Ctrl+Z. Daarom een bevestiging waarin de naam staat —
   * je klikt in dit scherm snel, en de knop zit naast "Afgerond".
   */
  const verwijderTaak = async () => {
    const akkoord = await bevestig({
      titel: 'Taak verwijderen?',
      tekst: `"${taak.naam || 'Deze taak'}" wordt definitief verwijderd uit Airtable.\n\n`
        + 'Dit kun je niet ongedaan maken. Is de taak gewoon klaar, gebruik dan Afgerond.',
      knop: 'Verwijderen',
      gevaarlijk: true,
    });
    if (!akkoord) return;
    setBezig(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/tasks/${encodeURIComponent(taak.id)}`, { method: 'DELETE' });
      await leesJson(r, 'Taak verwijderen');
      toast.success('Taak verwijderd');
      onClose();
      // Het scherm haalt zijn eigen taken opnieuw op; zonder deze melding blijft
      // het blok anders staan tot de volgende ronde.
      if (typeof onVerwijderd === 'function') onVerwijderd(taak.id);
    } catch (e) {
      meldFout(e, 'Taak verwijderen');
    } finally {
      setBezig(false);
    }
  };

  // Losse variant van `doe` die het paneel láát staan: uren en deadline pas je meestal in
  // één adem samen aan ("4 uur, moet voor donderdag"), en dan is dichtklappen na het
  // eerste veld alleen maar opnieuw klikken.
  const bewaar = async (velden, melding) => {
    setBezig(true);
    await onWijzig(taak.id, velden, { melding });
    setBezig(false);
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}>
      <Box sx={{ p: 2, backgroundColor: `${kleur}18`, borderBottom: `3px solid ${kleur}` }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Chip label={taak.prio || taak.type || 'Geen prio'} size="small" sx={{ mb: 1, fontWeight: 700, backgroundColor: '#fff' }} />
            {/* Taaknaam direct te bewerken (feedback #52): klik, typ, Enter of wegklikken slaat op. */}
            {taak.type === 'Afspraak' ? (
              <Typography sx={{ fontFamily: '"Manrope", system-ui, sans-serif', fontSize: '1.05rem', fontWeight: 800, color: '#2d3335' }}>
                {taak.name}
              </Typography>
            ) : (
              <Tooltip title="Klik om de taaknaam te wijzigen" arrow placement="top-start">
                <span>
                  <BewaarVeld
                    waarde={taak.name || ''}
                    breedte="100%"
                    disabled={bezig}
                    label="Taaknaam"
                    plaatshouder="Taaknaam"
                    onBewaar={(v) => {
                      const naam = String(v || '').trim();
                      if (!naam) return false;
                      return bewaar({ NaamTaak: naam }, 'Taaknaam gewijzigd');
                    }}
                  />
                </span>
              </Tooltip>
            )}
            {taak.project && (
              <Typography sx={{ fontSize: '0.78rem', color: '#556071' }}>📁 {taak.project}</Typography>
            )}
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Sluiten"><CloseIcon /></IconButton>
        </Box>
      </Box>

      <Box sx={{ p: 2, flex: 1, overflowY: 'auto' }}>
        <Regel label="Loopt">
          {datumLabel(taak.startDate)}
          {taak.startTime ? ` ${taak.startTime}${taak.endTime ? `–${taak.endTime}` : ''}` : ` — ${datumLabel(taak.endDate)}`}
          {!taak.startTime && (
            <Typography component="span" sx={{ color: '#94a3b8', fontSize: '0.75rem', ml: 0.75 }}>
              ({dagen.length} {dagen.length === 1 ? 'werkdag' : 'werkdagen'})
            </Typography>
          )}
        </Regel>

        <Regel label="Uren">
          {uitOutlook ? (
            <>
              {afspraakUrenLabel}
              <Typography component="span" sx={{ color: '#94a3b8', fontSize: '0.75rem', ml: 0.75 }}>
                (telt mee via de agenda-uren in de capaciteitsrij)
              </Typography>
            </>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
              <BewaarVeld
                waarde={taak.hours > 0 ? taak.hours : ''}
                breedte={78}
                disabled={bezig}
                label="Uren werk"
                plaatshouder="—"
                onBewaar={(ingevoerd) => {
                  // "1,5" is hoe je het hier intypt; Airtable wil er een getal van maken.
                  const schoon = String(ingevoerd).replace(',', '.').trim();
                  if (schoon === '') return bewaar({ UrenWerk: null }, 'Uren gewist');
                  const getal = Number(schoon);
                  if (!Number.isFinite(getal) || getal < 0) {
                    toast.error('Vul een aantal uren in, bijvoorbeeld 4 of 1,5');
                    return false;
                  }
                  return bewaar({ UrenWerk: getal }, `Uren → ${getal}`);
                }}
              />
              <Typography component="span" sx={{ fontSize: '0.78rem', color: '#556071' }}>
                uur{dagen.length > 1 ? ` · ${perDag}u per dag` : ''}
              </Typography>
              {estimated && (
                <Typography component="span" sx={{ color: '#f59e0b', fontSize: '0.72rem' }}>
                  nu geschat op {uren}u
                </Typography>
              )}
            </Box>
          )}
        </Regel>

        <Regel label="Deadline">
          {uitOutlook ? (taak.deadline ? datumLabel(taak.deadline) : '—') : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
              <BewaarVeld
                type="date"
                waarde={String(taak.deadline || '').slice(0, 10)}
                breedte={150}
                disabled={bezig}
                label="Deadline"
                onBewaar={(ingevoerd) => (ingevoerd
                  ? bewaar({ Deadline: ingevoerd }, `Deadline → ${datumLabel(ingevoerd)}`)
                  : bewaar({ Deadline: null }, 'Deadline gewist'))}
              />
              <Select
                size="small"
                value={DEADLINE_SOORTEN.includes(taak.deadlineType) ? taak.deadlineType : ''}
                displayEmpty
                disabled={bezig}
                // Soort mee kunnen zetten is geen luxe: alleen Hard en Klant zetten een
                // taak in ACHTERSTALLIG zodra de datum verstrijkt. Een deadline invullen
                // zonder soort levert een datum op waar het scherm niets mee doet.
                onChange={(e) => bewaar({ TypeDeadline: e.target.value }, `Soort deadline → ${e.target.value}`)}
                sx={{ minWidth: 104, '& .MuiSelect-select': { fontSize: '0.78rem', py: 0.6 } }}
              >
                <MenuItem value="" disabled sx={{ fontSize: '0.78rem' }}>Soort…</MenuItem>
                {DEADLINE_SOORTEN.map((s) => (
                  <MenuItem key={s} value={s} sx={{ fontSize: '0.78rem' }}>{s}</MenuItem>
                ))}
              </Select>
              {over > 0 && (
                <Typography component="span" sx={{ color: '#b91c1c', fontWeight: 700, fontSize: '0.75rem' }}>
                  {over} werkdagen over
                </Typography>
              )}
            </Box>
          )}
        </Regel>

        {taak.projectleider && <Regel label="Projectleider">{taak.projectleider}</Regel>}
        {taak.deliverable && <Regel label="Deliverable">{taak.deliverable}</Regel>}
        {/* Toewijzen aan iemand anders (Raijmond, 24 aug): na verslepen was er geen weg
            meer om de uitvoerder te wijzigen zonder Airtable. Zelfde PATCH-pad als slepen. */}
        <Regel label="Toegewezen">
          {onWijzig && personen.length && !String(taak.id || '').startsWith('outlook:') ? (
            <Select size="small" variant="standard" displayEmpty disableUnderline
              value={(() => {
                const huidige = String((taak.assignees || [])[0] || taak.assignee || '').toLowerCase();
                const p = personen.find((x) => String(x.firstName || x.name || '').toLowerCase() === huidige
                  || String(x.name || '').toLowerCase().startsWith(huidige) && huidige);
                return p?.id || '';
              })()}
              onChange={(e) => {
                const p = personen.find((x) => x.id === e.target.value);
                if (!p) return;
                onWijzig(taak.id, { Assignee: [p.id] }, { melding: `Toegewezen aan ${p.firstName || p.name}` });
              }}
              renderValue={(v) => {
                const p = personen.find((x) => x.id === v);
                return <span style={{ fontSize: '0.8rem' }}>{p ? (p.firstName || p.name) : ((taak.assignees || []).join(', ') || taak.assignee || '— kies uitvoerder')}</span>;
              }}
              sx={{ fontSize: '0.8rem', '& .MuiSelect-select': { py: 0.25 } }}>
              {[...personen].sort((a, b) => String(a.firstName || a.name).localeCompare(String(b.firstName || b.name))).map((p) => (
                <MenuItem key={p.id} value={p.id} sx={{ fontSize: '0.85rem' }}>{p.firstName || p.name}</MenuItem>
              ))}
            </Select>
          ) : ((taak.assignees || []).join(', ') || taak.assignee || '—')}
        </Regel>
        {taak.opmerking && <Regel label="Opmerking">{taak.opmerking}</Regel>}
        {/* Feedback (link + opmerkingen klant) — verplicht-ish bij v2/DEF, voor andere taken op verzoek. */}
        {(taak.feedbackVereist || taak.feedbackLink || taak.feedbackTekst) && !String(taak.id || '').startsWith('outlook:') && (
          <FeedbackVeld taak={taak} onLokaal={onLokaal} />
        )}
        {taak.lastModifiedBy && (
          <Regel label="Laatst gewijzigd">
            {taak.lastModifiedBy}{taak.lastModified ? ` · ${datumLabel(taak.lastModified)}` : ''}
          </Regel>
        )}

        <Divider sx={{ my: 1.5 }} />

        {/* Een afspraak uit de agenda heeft hier niets te kiezen: prioriteit, afronden en
            verzetten bestaan alleen voor Airtable-taken. De knoppen tonen en ze dan laten
            weigeren is een belofte die je niet nakomt. */}
        {uitOutlook ? (
          <Typography sx={{ fontSize: '0.78rem', color: '#556071' }}>
            📅 Deze afspraak komt rechtstreeks uit Outlook. Aanpassen doe je in je agenda —
            de dagstart leest hem alleen.
          </Typography>
        ) : (
        <Regel label="Prioriteit">
          <Select
            size="small"
            fullWidth
            value={PRIO_OPTIES.includes(taak.prio) ? taak.prio : ''}
            displayEmpty
            disabled={bezig}
            // Mét terug-pad: dezelfde undo als rechtsklikken op het blok —
            // een prio-wissel in het paneel was de enige plek zonder ongedaan-maken.
            onChange={(e) => doe({ PrioNr: e.target.value }, `Prioriteit → ${e.target.value}`,
              { PrioNr: taak.prio || 'Geen prio' })}
            sx={{ fontSize: '0.82rem' }}
          >
            <MenuItem value="" disabled><ListItemText primary="Kies…" /></MenuItem>
            {PRIO_OPTIES.map((p) => <MenuItem key={p} value={p} sx={{ fontSize: '0.82rem' }}>{p}</MenuItem>)}
          </Select>
        </Regel>
        )}

        {!uitOutlook && (
          <Regel label="Privé">
            <Button size="small" variant={priveAan ? 'contained' : 'outlined'} onClick={wisselPrive}
              disabled={privePending}
              sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.78rem' }}>
              {priveAan ? '🔒 Privé — alleen jij ziet deze taak' : '🔓 Zichtbaar voor iedereen'}
            </Button>
          </Regel>
        )}
      </Box>

      {!uitOutlook && (
      <Box sx={{ p: 1.5, borderTop: '1px solid #e8edf2', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Button
          size="small"
          variant="contained"
          startIcon={<TaskAltIcon />}
          disabled={bezig}
          // Afgerond staat twee keer in Airtable: de checkbox én de waarde in PrioNr.
          // Zet je er één, dan verdwijnt de taak hier wél en in de capaciteit niet.
          onClick={() => doe({ Afgerond: true, PrioNr: 'Afgerond' }, 'Afgerond',
            { Afgerond: false, PrioNr: taak.prio || 'Geen prio' })}
          sx={{ textTransform: 'none', backgroundColor: '#16a34a', '&:hover': { backgroundColor: '#15803d' } }}
        >
          Afgerond
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<SendIcon />}
          disabled={bezig}
          // Opgestuurd is géén Afgerond: het werk is de deur uit maar de klant kan nog
          // terugkomen. Daarom alleen PrioNr, de checkbox blijft uit.
          onClick={() => doe({ PrioNr: 'Opgestuurd' }, 'Opgestuurd', { PrioNr: taak.prio || 'Geen prio' })}
          sx={{ textTransform: 'none' }}
        >
          Opgestuurd
        </Button>
        {onNaarVandaag && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<TodayIcon />}
            disabled={bezig}
            onClick={() => { onNaarVandaag(taak); onClose(); }}
            sx={{ textTransform: 'none' }}
          >
            Naar vandaag
          </Button>
        )}
        {onDupliceer && (
          <Tooltip title="Zelfde taak nog een keer, op dezelfde dagen. Tip: Alt of Shift ingedrukt houden tijdens het slepen kopieert naar een andere dag.">
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                disabled={bezig}
                onClick={async () => { setBezig(true); try { await onDupliceer(taak); } finally { setBezig(false); } }}
                sx={{ textTransform: 'none' }}
              >
                Dupliceren
              </Button>
            </span>
          </Tooltip>
        )}
        <Button
          size="small"
          startIcon={<DeleteOutlineIcon />}
          disabled={bezig}
          onClick={verwijderTaak}
          sx={{ textTransform: 'none', color: '#b91c1c', ml: 'auto' }}
        >
          Verwijderen
        </Button>
        {taak.projectId && onNaarProject && (
          <Button size="small" startIcon={<OpenInNewIcon />} onClick={() => onNaarProject(taak.projectId)} sx={{ textTransform: 'none' }}>
            Project
          </Button>
        )}
      </Box>
      )}
    </Drawer>
  );
}
